import React, { Component } from 'react';
import { Formik } from "formik";
import qs from "qs";
import { config } from './Constants'
import { withTranslation } from "react-i18next";
import LanguagePicker from "./LanguagePicker";
import validator from 'validator'
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import './RegisterPage.css';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { Link } from 'react-router-dom';
import posthog from 'posthog-js';

// The register endpoint answers 400s either with {"reason": "..."} (duplicate
// email) or with a plain JSON string ("Invalid email", "Invalid language",
// "Missing required field") — handle both so every failure gets a visible,
// trackable reason instead of an empty alert().
const registerFailureReason = (status, responseText) => {
	let body = null;
	try {
		body = JSON.parse(responseText);
	} catch (e) {
		// non-JSON body: fall through to the status-based reason
	}
	if (body && typeof body === 'object' && body.reason) {
		return body.reason;
	}
	if (typeof body === 'string') {
		if (body.includes('Invalid email')) return 'invalid_email';
		if (body.includes('Invalid language')) return 'invalid_language';
		if (body.includes('Missing required field')) return 'missing_field';
	}
	return `http_${status}`;
}

// PostHog being blocked or broken must never break the signup (TAK-27):
// every analytics call from this page goes through this guard.
const safeCapture = (event, properties) => {
	try {
		posthog.capture(event, properties);
	} catch (e) { /* analytics unavailable — non-fatal */ }
}

const trackRegisterFailure = (reason) => {
	window.gtag('event', 'sign_up_failed', { 'reason': reason });
	safeCapture('register_failed', { reason });
}

const DuplicateEmail = ({ t }) => {
	return <div className="email requirements">
		<div>{t('create-account.errors.duplicated-email')} <Link to='/login'>Login</Link></div>
	</div>
}

// A/B test (PostHog experiment): skip the "Select solutions to activate" step
// and send new signups straight to the account form — solutions keep their
// defaults and remain activatable later from the App Store. "control" renders
// the current two-step flow. `?flow=control|test` overrides the flag so e2e
// runs and manual checks stay deterministic.
const SOLUTIONS_STEP_FLAG = 'register-defer-solutions';

// Campaign links don't all use the canonical ids: the reservations landing
// pages link with ?solution=reservations, some with ?solution=survey(s), and
// broken templates have produced bare 'e'/'t'. Normalize the known aliases so
// the promised solution is preselected; unknown values fall through and hit
// the menus fallback below.
const SOLUTION_PARAM_ALIASES = { reservations: 'table-reservation', survey: 'surveys' };

class RegisterPage extends Component {
	constructor(props) {
		super(props);

		this.rawSolutionParam = qs.parse(window.location.search, { ignoreQueryPrefix: true }).solution || 'all';
		this.solution = SOLUTION_PARAM_ALIASES[this.rawSolutionParam] || this.rawSolutionParam;
		// this.solutions = [this.solution]

		this.referral = qs.parse(window.location.search, { ignoreQueryPrefix: true }).referral || null;

		// Google Ads auto-tagging puts exactly one of gclid/wbraid/gbraid on the
		// landing URL of a paid click. Kept as {kind, value} and sent with the
		// register POST so the API can upload the signup as a conversion
		// server-side (TAK-50) — server-side because the browser gtag is
		// consent-gated and misses most visitors.
		const adParams = qs.parse(window.location.search, { ignoreQueryPrefix: true });
		this.adClickId = /** @type {null | {kind: string, value: string}} */ (null);
		for (const kind of ['gclid', 'wbraid', 'gbraid']) {
			const value = adParams[kind];
			if (typeof value === 'string' && /^[A-Za-z0-9_-]{10,300}$/.test(value)) {
				this.adClickId = { kind, value };
				break;
			}
		}

		const menuSolution = ["digital-menu", "all"].includes(this.solution);
		const websiteSolution = this.solution === "website";
		const tableReservationSolution = this.solution === "table-reservation";
		const surveysSolution = this.solution === "surveys";
		// Unknown/typo'd ?solution= values would otherwise register an account
		// with zero solutions activated (nothing to see post-login). Digital
		// menus is the core product, so default to it.
		const nothingPreselected = !menuSolution && !websiteSolution && !tableReservationSolution && !surveysSolution;

		this.state = {
			registered: false,
			showPassword: false,
			email: "",
			defaultEmail: "",
			tier: qs.parse(window.location.search, { ignoreQueryPrefix: true }).tier || "standard",
			view: "solutions",
			// Resolved to "control"/"test" once the PostHog flag loads (or the
			// fallback timer fires); nothing renders until then to avoid
			// flashing the solutions step at variant users.
			flowVariant: "pending",
			menuSolution: menuSolution || nothingPreselected,
			websiteSolution,
			orderingSolution: false,
			tableReservationSolution,
			surveysSolution
		};
	}

	componentDidMount() {
		window.gtag('event', 'start_sign_up', { 'product': this.solution || 'none' });
		const forcedEmail = qs.parse(window.location.search, { ignoreQueryPrefix: true }).email;
		if (forcedEmail != null) {
			this.setState({ defaultEmail: forcedEmail })
		}
		this.resolveFlowVariant();
	}

	componentWillUnmount() {
		// Leaving /register before the flag settles must not fire a phantom
		// register_view from the fallback timer (or from a late decide).
		this.unmounted = true;
		if (this.flowFallbackTimer) clearTimeout(this.flowFallbackTimer);
	}

	resolveFlowVariant() {
		// e2e and manual checks force an arm directly: no flag evaluation, no
		// exposure event — forced runs must stay out of the experiment data.
		const flowOverride = qs.parse(window.location.search, { ignoreQueryPrefix: true }).flow;
		if (flowOverride === "control" || flowOverride === "test") {
			this.applyFlowVariant(flowOverride, { forced: true });
			return;
		}
		try {
			// Fires immediately when flags are already loaded, otherwise when
			// the decide response lands.
			posthog.onFeatureFlags(() => this.settleFlowVariant());
		} catch (e) {
			// PostHog broken/uninitialized: render the control flow rather
			// than lose the signup (TAK-27).
			this.applyFlowVariant("control");
			return;
		}
		// Deadline for the flag fetch: past it the control flow renders so a
		// slow or blocked decide never blanks the page. Measured pv→exposure
		// latency since launch: 0.2–2.4s, median ≈1s — most sessions settle
		// inside the deadline, and module-scope init (routes.js) shifts the
		// whole range earlier.
		this.flowFallbackTimer = setTimeout(() => this.settleFlowVariant(), 1500);
	}

	// Resolve the variant exactly once: normally from a loaded flag; or, when
	// the fallback deadline already rendered control, from a late flag —
	// exposing the user only if the assignment matches what they saw.
	settleFlowVariant() {
		if (this.unmounted) return;
		let flag = null;
		const late = this.flowApplied;
		try {
			// send_event: false — the exposure is recorded explicitly below
			// instead of by posthog-js, so an evaluation that comes back empty
			// (the /flags endpoint occasionally answers 200 with no flags at
			// all) never emits a $feature_flag_called with an undefined
			// response.
			flag = posthog.getFeatureFlag(SOLUTIONS_STEP_FLAG, { send_event: false });
		} catch (e) {
			if (!late) {
				this.applyFlowVariant("control");
				return;
			}
		}
		if ((flag === "control" || flag === "test") && !this.exposureCaptured) {
			// Record the exposure only if it matches what the user actually
			// saw: on the normal path the flag decided the flow; after the
			// fallback showed control, a late "test" assignment stays
			// invisible to the experiment instead of counting a control
			// experience as a test conversion.
			if (!late || this.state.flowVariant === flag) {
				this.exposureCaptured = true;
				safeCapture('$feature_flag_called', {
					$feature_flag: SOLUTIONS_STEP_FLAG,
					$feature_flag_response: flag,
				});
			}
		}
		if (!late) {
			this.applyFlowVariant(flag === "test" ? "test" : "control", { flagsLoaded: flag != null });
		}
	}

	// One-shot: once the flow is applied a late-loading flag never switches the
	// steps the user already started on.
	applyFlowVariant(variant, { forced = false, flagsLoaded = true } = {}) {
		if (this.flowApplied) return;
		this.flowApplied = true;
		if (this.flowFallbackTimer) clearTimeout(this.flowFallbackTimer);
		this.setState({
			flowVariant: variant,
			view: variant === "test" ? "email" : this.state.view
		});
		safeCapture('register_view', {
			variant,
			solution_param: this.rawSolutionParam,
			// forced: `?flow=` override (e2e/dev), not a real assignment;
			// flags_pending: rendered on the fallback before decide answered.
			...(forced ? { overridden: true } : {}),
			...(flagsLoaded ? {} : { flags_pending: true }),
		});
	}

	render() {
		const { t, i18n } = this.props;

		const validationFunction = function accountFormValidation(values) {
			const errors = {};
			if (!values.email) {
				errors.email = t('create-account.errors.required');
			} else if (!validator.isEmail(values.email)) {
				errors.email = t('create-account.errors.invalid-email');
			}
			if (!values.password) {
				errors.password = "Required";
			} else if (!validator.isStrongPassword(values.password, { minLength: 0, minLowercase: 0, minUppercase: 0, minNumbers: 0, minSymbols: 0 })) {
				errors.password = "weak"
			}

			return errors;
		}

		const EmailRequirements = ({ email, show }) => {
			return <div className={"email requirements" + (show ? "" : " hide")}>
				{(!email) ?
					<div className="requirement">{t('create-account.errors.required')}</div>
					:
					<div className="requirement">{t('create-account.errors.invalid-email')}</div>
				}
			</div>
		}

		return <div id="login-page" className="register">
			<div className="lg-screen" style={{ position: "absolute", width: "100%", zIndex: "1", top: "1rem", paddingLeft: "1rem", display: "flex", justifyContent: "space-between" }}>
				<a className="appbar-logo" href={config.url.LANDING}>
					<img alt="Take a Seat logo" src={`https://takeaseat.io/media/icons/tas-230526.svg`} />
					<div>Take a Seat</div>
				</a>
				<LanguagePicker />
			</div>
			<div>
				<picture>
					<source srcSet={"/static/media/register-bg.webp"} type="image/webp" width="0" height="0" loading="lazy" />
					<source srcSet={"/static/media/register-bg.jpg"} type="image/jpeg" width="0" height="0" loading="lazy" />
					<img alt="Setup process" style={{ width: "100vw", height: "100vh", filter: "blur(7px)", objectFit: "cover" }} width="0" height="0" loading="lazy" />
				</picture>
			</div>
			<div className={"login-page-content"}>
				<div className="logo"></div>
				{this.state.flowVariant === "pending" ?
					<div style={{ minHeight: "15rem", display: "flex", alignItems: "center", justifyContent: "center" }}>
						<div className="loader">Loading...</div>
					</div>
				:
				this.state.view === "solutions" ? <>
					<h1>{t('solutions.title')}</h1>
					<div style={{ minHeight: "15rem", justifyContent: "center", alignItems: "center", display: "flex", flexDirection: "column" }}>
						<div className={"solution-selector" + (this.state.menuSolution ? " selected" : "")} onClick={() => {
							this.setState(prev => { return { ...prev, menuSolution: !prev.menuSolution } })
						}}>
							<div>{t('solutions.menus')}</div>
							<div>{this.state.menuSolution ? <TaskAltIcon /> : <RadioButtonUncheckedIcon />} </div>
						</div>
						<div className={"solution-selector" + (this.state.orderingSolution ? " selected" : "")} onClick={() => {
							this.setState(prev => { return { ...prev, orderingSolution: !prev.orderingSolution } })
						}}>
							<div>{t('solutions.order')}</div>
							<div>{this.state.orderingSolution ? <TaskAltIcon /> : <RadioButtonUncheckedIcon />} </div>
						</div>
						<div className={"solution-selector" + (this.state.websiteSolution ? " selected" : "")} onClick={() => {
							this.setState(prev => { return { ...prev, websiteSolution: !prev.websiteSolution } })
						}}>
							<div>{t('solutions.website')}</div>
							<div>{this.state.websiteSolution ? <TaskAltIcon /> : <RadioButtonUncheckedIcon />} </div>
						</div>
						<div className={"solution-selector" + (this.state.tableReservationSolution ? " selected" : "")} onClick={() => {
							this.setState(prev => { return { ...prev, tableReservationSolution: !prev.tableReservationSolution } })
						}}>
							<div>{t('solutions.resa')}</div>
							<div>{this.state.tableReservationSolution ? <TaskAltIcon /> : <RadioButtonUncheckedIcon />} </div>
						</div>
						<div className={"solution-selector" + (this.state.surveysSolution ? " selected" : "")} onClick={() => {
							this.setState(prev => { return { ...prev, surveysSolution: !prev.surveysSolution } })
						}}>
							<div>{t('solutions.surveys')}</div>
							<div>{this.state.surveysSolution ? <TaskAltIcon /> : <RadioButtonUncheckedIcon />} </div>
						</div>
						<div className="tas-button" style={{ width: "100%", padding: "1rem" }}
							onClick={() => this.setState({ view: "email" })}>{t('create')}</div>
					</div>
				</> :
					(this.state.registered ? <>
						<h1>{t('create-account.validation-title')}</h1>
						<div style={{ minHeight: "15rem", justifyContent: "center", alignItems: "center", display: "flex", flexDirection: "column" }}>
							<div style={{ margin: "1rem", textAlign: "center" }}>
								<p style={{ marginBottom: "0" }}>{t('sent-verification-email')}</p>
							</div>
							{this.state.email.endsWith("gmail.com") && <a href="https://gmail.com" target="_blank" style={{ display: "flex" }}> <div>{t('to-emails')}</div> <div>&#160;Gmail&#160;</div> <MailOutlineIcon /></a>}
							{this.state.email.endsWith("yahoo.com") && <a href="https://login.yahoo.com" target="_blank" style={{ display: "flex" }}> <div>{t('to-emails')}</div> <div>&#160;Yahoo mails&#160;</div><MailOutlineIcon /></a>}
							{this.state.email.endsWith("hotmail.com") && <a href="https://outlook.live.com/mail" target="_blank" style={{ display: "flex" }}> <div>{t('to-emails')}</div> <div>&#160;Hotmail&#160;</div><MailOutlineIcon /></a>}
							{this.state.email.endsWith("icloud.com") && <a href="https://www.icloud.com/mail" target="_blank" style={{ display: "flex" }}> <div>{t('to-emails')}</div> <div>&#160;iCloud emails&#160;</div> <MailOutlineIcon /></a>}
						</div>
						<div style={{ fontSize: "80%", color: "grey" }}>
							<div>{t('sent-to')}{this.state.email}</div>
							<div><a href="javascript:void(0);" onClick={() => this.setState({ registered: false, defaultEmail: this.state.email, defaultPassword: this.state.password })}>{t('update-email')}</a></div>
						</div>
					</> : <>
						<h1>{t('create-account.value-title')}</h1>
						<div className="register-subtitle">{t('create-account.no-card')}</div>
						<div className="login-google" onClick={() => {
							const tasTier = (this.state.tier === 'professional') ? 'gold' : 'standard'

							this.setState({ isSubmittingGoogle: true })
							window.gtag('event', 'sign_up', { 'product': this.solution || 'none' });
							// We must set the default tier user_properties at some point for
							// the Google Analytics user. Even though the tier cannot be
							// changed during registration, it makes sense to set it when the
							// user first signs up.
							window.gtag('set', 'user_properties', { 'tier': 'standard' });
							window.gtag('event', 'conversion', { 'send_to': 'AW-642020249/qccQCNyj0e4YEJnnkbIC' });
							var window_location_href = `${config.url.API}/auth/google?tier=${tasTier}&solution=${this.solution}`
							if (this.state.menuSolution) window_location_href += "&solutions[]=digital-menu"
							if (this.state.websiteSolution) window_location_href += "&solutions[]=public-page"
							if (this.state.orderingSolution) window_location_href += "&solutions[]=ordering"
							if (this.state.tableReservationSolution) window_location_href += "&solutions[]=table-reservation"
							if (this.state.surveysSolution) window_location_href += "&solutions[]=surveys"

							window.location.href = window_location_href
						}} >
							{this.state.isSubmittingGoogle ? <div className="loader">Loading...</div> :
								<>
									<img src='/static/media/icons/google-icon.png' alt="Google icon" />
									<span>{t("google-sign-in")}</span>
								</>}
						</div>
						<div className="login-sep"><span>OR</span></div>
						<Formik
							initialValues={{
								email: this.state.defaultEmail,
								password: this.state.defaultPassword || ''
							}}
							validate={validationFunction}
							onSubmit={(values, { setTouched, setSubmitting, setStatus }) => {
								setStatus({}); // clear any previous submit error
								const tasTier = (this.state.tier === 'professional') ? 'gold' : 'standard'
								var xhr = new XMLHttpRequest();
								xhr.open("POST", `${config.url.API}/register`);
								xhr.setRequestHeader('Content-Type', 'application/json');
								xhr.withCredentials = true;
								xhr.onload = function (e) {
									if (xhr.readyState === 4) {
										if (xhr.status === 200) {
											const ga_user = JSON.parse(xhr.responseText)["ga_user"];
											// First set the user_id to associate this session and future sessions to the
											// same underlying TaS user.
											window._env_.GA_CONFIGS.forEach((c) => {
												window.gtag("config", c, { 'user_id': ga_user });
											});

											// Method tag can be things like Facebook account, Google account, email.
											window.gtag('event', 'sign_up_unverified', { 'product': this.solution || 'none' });
											safeCapture('register_success');
											// We must set the default tier user_properties at some point for
											// the Google Analytics user. Even though the tier cannot be
											// changed during registration, it makes sense to set it when the
											// user first signs up.
											window.gtag('set', 'user_properties', { 'tier': 'standard' });
											window.gtag('event', 'conversion', { 'send_to': 'AW-642020249/wS2kCLeW4MsCEJnnkbIC' });

											// const restaurantId = JSON.parse(xhr.responseText)["id"];
											this.setState({ registered: true });
										} else if (xhr.status === 400) {
											const reason = registerFailureReason(xhr.status, xhr.responseText);
											trackRegisterFailure(reason);
											if (reason === "duplicate_email") {
												setStatus({ "email": "duplicate_email" });
											} else {
												setStatus({ "error": reason });
											}
											setSubmitting(false);
										} else {
											trackRegisterFailure(`http_${xhr.status}`);
											setStatus({ "error": `http_${xhr.status}` });
											setSubmitting(false);
										}
									}
								}.bind(this);
								xhr.onerror = function (e) {
									trackRegisterFailure('network_error');
									setStatus({ "error": "network_error" });
									setSubmitting(false);
								};
								xhr.send(JSON.stringify({
									"email": values.email,
									"password": values.password,
									"lang": (i18n.language) ? i18n.language : "",
									"solution": 'all',
									"solutions": [
										this.state.menuSolution ? "digital-menu" : null,
										this.state.websiteSolution ? "public-page" : null,
										this.state.orderingSolution ? "ordering" : null,
										this.state.tableReservationSolution ? "table-reservation" : null,
										this.state.surveysSolution ? "surveys" : null,
									].filter(ele => ele != null),
									"referral": this.referral,
									"tier": tasTier,
									...(this.adClickId ? { [this.adClickId.kind]: this.adClickId.value } : {})
								}));
							}}
						>{({
							values,
							errors,
							touched,
							handleChange,
							handleSubmit,
							handleBlur,
							status,
							setStatus,
							isSubmitting,
						}) => {
							const emailError = errors.email && touched.email
							const passwordError = errors.password && touched.password
							return <form onSubmit={handleSubmit}>
								<fieldset>
									<label htmlFor="email">{t('create-account.email')} <EmailRequirements email={values.email} show={emailError} /></label>
									<input type="email"
										name="email"
										id="email"
										autoComplete="email"
										onChange={e => {
											setStatus('');
											this.setState({ email: e.target.value })
											handleChange(e);
										}}
										style={{ border: "1px solid lightgrey", borderRadius: "4px" }}
										onBlur={handleBlur}
										value={values.email}
										className={emailError ? "has-error" : ""}
										tabIndex="1" autoFocus="true" />

									{(status && status.email === "duplicate_email") && <DuplicateEmail t={t} />}
									<label htmlFor="password">
										{t('create-account.password')}
										{passwordError &&
											<div className="requirements">
												<div className="requirement">{errors.password === "Required" ? t("create-account.errors.required") : t("create-account.errors.weak-password")}</div>
											</div>
										}</label>
									<div className="pwd-field">
										<input
											type={this.state.showPassword ? "text" : "password"}
											name="password"
											id="password"
											autoComplete="new-password"
											onChange={e => {
												setStatus('');
												this.setState({ password: e.target.value })
												handleChange(e);
											}}
											onBlur={handleBlur}
											value={values.password}
											className={passwordError ? "has-error" : ""}
											tabIndex="2" />
										<div className="visibility-icon">{
											(this.state.showPassword) ?
												<VisibilityIcon onClick={() => this.setState({ showPassword: false })} />
												:
												<VisibilityOffIcon onClick={() => this.setState({ showPassword: true })} />
										}</div>
									</div>
									{this.referral && <>
										<label htmlFor="referral">Referral ID:</label>
										<div className="pwd-field">
											<input
												type="text"
												name="referral"
												id="referral"
												value={this.referral}
												disabled={true}
												tabIndex="2" />
										</div>
									</>}
									{status && status.error && <div className="submit-error">
										{status.error === "invalid_email" ? t('create-account.errors.invalid-email')
											: status.error === "network_error" ? t('create-account.errors.network')
											: t('create-account.errors.submit-failed')}
									</div>}
									<button type="submit" className="tas-button" disabled={isSubmitting}> {isSubmitting ? <div className="loader">Loading...</div> : t('create')}</button>
								</fieldset>
							</form>
						}}
						</Formik>
						<div className="auth-switch"> {t("already-user")} <Link className="auth-switch-link" to="/login">{t("login")}</Link> </div>
					</>
					)}
			</div>
		</div >
	}
}

const RegisterPageTranslated = withTranslation('register')(RegisterPage)

export default RegisterPageTranslated;
